import { useCallback, useRef, useState } from 'react';
import { Column } from './types';

// Column resize hook
export type ColumnResizeResult = {
  resizingColumn: string | null;
  handleResizeStart: (e: React.MouseEvent, columnKey: string) => void;
};

export const useColumnResize = (
  columns: Column[],
  onColumnsChange: (columns: Column[]) => void,
  minWidth: number = 200,
  maxWidth: number = 500
): ColumnResizeResult => {
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, columnKey: string) => {
      e.preventDefault();
      e.stopPropagation();

      const column = columns.find((col) => col.key === columnKey);
      if (!column) {
        return;
      }

      setResizingColumn(columnKey);
      startXRef.current = e.clientX;
      startWidthRef.current = column.width ?? minWidth;

      const handleMouseMove = (e: MouseEvent) => {
        const deltaX = e.clientX - startXRef.current;
        const newWidth = Math.max(
          minWidth,
          Math.min(maxWidth, startWidthRef.current + deltaX)
        );

        const newColumns = columns.map((col) =>
          col.key === columnKey ? { ...col, width: newWidth } : col
        );
        onColumnsChange(newColumns);
      };

      const handleMouseUp = () => {
        setResizingColumn(null);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [columns, onColumnsChange, minWidth, maxWidth]
  );

  return {
    resizingColumn,
    handleResizeStart,
  };
};
